import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FAF8F5] to-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="mb-6 border-[#6B0B0B] text-[#6B0B0B] hover:bg-[#6B0B0B] hover:text-white"
        >
          <ArrowLeft className="mr-2" size={18} />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-lg border border-[#D4AF37]/20 p-8 md:p-12">
          <h1 className="text-4xl font-bold text-[#6B0B0B] mb-2">Terms of Service</h1>
          <p className="text-gray-600 mb-6">Website use and booking terms</p>
          <p className="text-sm text-gray-500 mb-8">Last updated: 23 October 2026</p>

          <div className="bg-[#FFF9F0] border-l-4 border-[#D4AF37] p-6 mb-8 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              By using the website or making a booking, you agree to these Terms. These Terms work together with our Privacy Policy, Cancellation and Refund Policy and Disclaimer. Nothing in these Terms limits any right you have under the Consumer Protection Act 68 of 2008 or any other law.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">1. About us</h2>
              <p className="text-gray-700 leading-relaxed">The tour operator is Cape Atlantic Tours, a private company registered in South Africa.</p>
              <ul className="text-gray-700 space-y-2 mt-3">
                <li><strong>Email:</strong> info@capeatiantictours.co.za</li>
                <li><strong>Telephone:</strong> 073 654 0090</li>
                <li><strong>Director:</strong> George Kagiso Monamodi</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">2. Bookings and how our contract is formed</h2>
              <ul className="text-gray-700 space-y-3">
                <li>A binding contract is formed only when we send you a written booking confirmation and your payment has been received.</li>
                <li>You must give us accurate and complete information. We are not responsible for problems caused by incorrect information.</li>
                <li>You must be 18 or older to make a booking.</li>
                <li>If your group includes anyone under 18, you confirm that you are their parent or legal guardian.</li>
                <li>Bookings are subject to availability. We may decline or cancel a booking before confirmation.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">3. Prices and payment</h2>
              <ul className="text-gray-700 space-y-3">
                <li>All prices are in South African Rand (ZAR) and include VAT where applicable.</li>
                <li>The price displayed at checkout is the price you pay. We do not add hidden charges.</li>
                <li>We have different rates for South African residents and international visitors. The resident rate applies to SA citizens and permanent residents who can show valid SA ID.</li>
                <li>Payment is made online through our secure payment provider.</li>
                <li>Prices can change, but a change will not affect a booking we have already confirmed.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">4. Cancellations, changes and refunds</h2>
              <p className="text-gray-700 leading-relaxed">
                You may cancel or change a booking in line with our Cancellation and Refund Policy. In terms of the Consumer Protection Act, you may cancel an advance booking, but we may charge a reasonable cancellation fee. No cancellation fee applies where you cannot honour the booking because of death or hospitalisation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">5. Itinerary, timing and changes by us</h2>
              <ul className="text-gray-700 space-y-3">
                <li>Timings and routes depend on weather, traffic, and attraction schedules. We may change the itinerary where reasonably necessary.</li>
                <li>If we have to make a significant change or cancel, we will tell you early and offer an alternative, reschedule or refund.</li>
                <li>Pick-up times are confirmed before the tour. Please be on time - we cannot wait beyond the stated time.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">6. Your responsibilities as a guest</h2>
              <ul className="text-gray-700 space-y-3">
                <li>You must carry valid travel and identity documents (passport or SA ID).</li>
                <li>You must follow the instructions of our guides and drivers, particularly on safety.</li>
                <li>You must tell us before the tour about any medical condition or mobility limitation.</li>
                <li>We may refuse to carry anyone whose behaviour is dangerous or disruptive, without refund.</li>
                <li>You are responsible for your belongings and liable for damage you cause.</li>
                <li>We strongly recommend travel and medical insurance.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">7. Safety, risk and third-party services</h2>
              <ul className="text-gray-700 space-y-3">
                <li>Tours may involve activities with inherent risk. By going on a tour you accept the risks we have disclosed.</li>
                <li>Parts of your tour may be provided by independent third parties with their own terms and conditions.</li>
                <li>We exercise reasonable care and skill in providing our services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">8. Limitation of liability</h2>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
                <p className="text-amber-900 font-semibold">Important:</p>
                <p className="text-amber-800">Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, for gross negligence, for fraud, or for any liability that cannot lawfully be excluded under the Consumer Protection Act.</p>
              </div>
              <ul className="text-gray-700 space-y-3">
                <li>We are not liable for loss not caused by our negligence or breach of contract.</li>
                <li>Where we are liable, our liability is limited to direct, foreseeable loss, capped at the amount you paid us.</li>
                <li>We are not liable for indirect or consequential loss to the extent the law allows.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">9. Events beyond our control</h2>
              <p className="text-gray-700 leading-relaxed">
                We are not in breach if we cannot perform because of events beyond our reasonable control, including severe weather, natural disasters, government action, strikes, or third-party cancellations. We will offer a reschedule or refund.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">10. Using our website</h2>
              <ul className="text-gray-700 space-y-3">
                <li>You may use the website only for lawful purposes.</li>
                <li>All content belongs to us and is protected by copyright.</li>
                <li>We try to keep the website available but do not guarantee uninterrupted access.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">11. Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                We process your personal information in line with our Privacy Policy and POPIA. By making a booking you acknowledge that you have read the Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">12. Complaints</h2>
              <p className="text-gray-700 leading-relaxed">
                If something goes wrong, please tell us as soon as possible. Email info@capeatiantictours.co.za within 14 days with your booking reference. We will acknowledge your complaint and aim to respond within 7 working days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">13. General</h2>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Governing law:</strong> South African law applies</li>
                <li><strong>Language:</strong> English version prevails</li>
                <li><strong>Updates:</strong> We may update these Terms from time to time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">14. Contact</h2>
              <div className="bg-[#FFF9F0] p-6 rounded-lg">
                <p className="text-gray-700"><strong>Cape Atlantic Tours</strong></p>
                <p className="text-gray-700">Email: info@capeatiantictours.co.za</p>
                <p className="text-gray-700">Telephone: 073 654 0090</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;